import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Autocomplete,
  Dialog,
  DialogContent,
  IconButton,
  Checkbox,
} from "@mui/material";
import { toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { BASE_URL } from "../../global";
import axiosWithToken from "../../utils/commonComponent/axiosTokenConfig";
import FlagIcon from "@mui/icons-material/Flag";
import CloseIcon from "@mui/icons-material/Close";

export const SUBTYPE_OPTIONS = [
  "Travelling",
  "Fuel Expense",
  "Toll / Fast tag & Parking",
  "Hotel / Lodging",
  "Food / Meals",
  "Labour DA",
  "Rent",
  "Material Purchase",
  "Equipment Hire (Tractor/ Tanker/ Local Vehicle)",
  "Repair & Maintenance",
  "Courier / Stationery",
  "Loading / Unloading Charges",
  "Medical Expenses",
  "Others",
  "Subcontractor Advance",
  "Subcontractor Diesel",
  "Porter Charges",
  "Hydra/Farana Loading / Unloading Charges",
  "Equipment - JCB / Water Tanker",
  "Staff / Labour Drinking Water",
  "Surveyor Charges",
];

const Subtypes = ({
  subtypesData,
  setSubtypesData,
  deletedIds,
  setDeletedIds,
  permissions,
  inputStates,
  saveDisabledCon,
  notRequster,
  hasRedFlag,
  selectedRows,
  setSelectedRows,
}) => {
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);

  const disbursedIds = useMemo(() => {
    try {
      const parsed = JSON.parse(inputStates?.disbursedDetails || "[]");
      return parsed.map((d) => d.id).filter(Boolean);
    } catch {
      return [];
    }
  }, [inputStates?.disbursedDetails]);

  const selectableRows = subtypesData.filter(
    (item) => !disbursedIds.includes(item.id),
  );

  const handleRowCheckbox = (row) => {
    if (disbursedIds.includes(row.id)) return;
    setSelectedRows((prev) =>
      prev.some((r) => r === row)
        ? prev.filter((r) => r !== row)
        : [...prev, row],
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(selectableRows);
    } else {
      setSelectedRows([]);
    }
  };

  const isAllSelected =
    selectableRows.length > 0 &&
    selectableRows.every((item) => selectedRows.some((r) => r === item));

  const isIndeterminate = selectedRows.length > 0 && !isAllSelected;

  const selectedRowsData = subtypesData.filter((item) =>
    selectedRows.includes(item),
  );

  const addSubtypeRow = () => {
    setSubtypesData([
      ...subtypesData,
      {
        subTypes: "",
        description: "",
        amount: "",
        remark: "",
        redFlagStatus: 0,
        redFlagDetails: "",
        documentUlr: "",
      },
    ]);
  };

  const handleSubtypeChange = (index, field, value) => {
    const updatedData = subtypesData.map((item, i) =>
      i === index ? { ...item, [field]: value } : item,
    );
    setSubtypesData(updatedData);
  };

  const copySubtypeRow = (index) => {
    const rowToCopy = subtypesData[index];
    const { id, ...copiedRow } = rowToCopy;
    const updatedData = [...subtypesData];
    updatedData.splice(index + 1, 0, { ...copiedRow });
    setSubtypesData(updatedData);
  };

  const deleteSubtypeRow = (index) => {
    const rowToDelete = subtypesData[index];
    if (rowToDelete.id) {
      setDeletedIds([...deletedIds, String(rowToDelete.id)]);
    }
    const updatedData = subtypesData.filter((_, i) => i !== index);
    setSubtypesData(updatedData);

    setSelectedRows((prev) =>
      prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i)),
    );
  };

  const uploadDocInRow = (index) => {
    const input = document.getElementById(`upload-doc-${index}`);
    if (input) input.click();
  };

  const toggleRedFlag = (index) => {
    const updated = [...subtypesData];
    updated[index].redFlagStatus = updated[index].redFlagStatus === 1 ? 0 : 1;
    if (updated[index].redFlagStatus === 0) {
      updated[index].redFlagDetails = "";
    }
    setSubtypesData(updated);
  };

  const handleRowFileUpload = (event, index) => {
    const file = event.target.files[0];
    if (!file) return;


    if (file.size > 3 * 1024 * 1024) {
      toast.error("File size exceeds 3 MB, cannot upload!");
      return;
    }

    const data = new FormData();
    data.append("fileToUpload", file);
    axiosWithToken
      .post(BASE_URL + "fileUpload/fileUpload.php", data)
      .then((res) => {
        if (res.status === 200) {
          const updatedData = [...subtypesData];
          updatedData[index] = { ...updatedData[index], documentUlr: res.data };
          setSubtypesData(updatedData);
          toast.success("File Uploaded Successfully!");
        }
      })
      .catch(() => toast.error("Something went wrong"));
  };

  const handleOpenEditModal = (index) => {
    setSelectedRowIndex(index);
    setEditModalOpen(true);
  };

  const totalAmount = subtypesData.reduce(
    (total, item) => total + parseFloat(item.amount || 0),
    0,
  );

  const showRedFlagColumn = permissions.l1 && inputStates?.l1Status == 0;
  const iconDisabled = saveDisabledCon || notRequster;
  const iconL1Disabled = saveDisabledCon || (notRequster && !permissions.l1);

  return (
    <Box p={2}>
      <Typography variant="h6" align="center" fontWeight="bold" mb={2}>
        Subtypes / Line Items
      </Typography>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={addSubtypeRow}
          disabled={saveDisabledCon || notRequster}
        >
          Add Subtypes
        </Button>
      </Box>

      {/* ✅ Optional: Show count of selected rows */}
      {selectedRows.length > 0 && (
        <Box mb={1}>
          <Typography variant="body2" color="primary">
            {selectedRows.length} row(s) selected
          </Typography>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {/* ✅ Select All Checkbox */}
              {inputStates?.h2Status === 1 && permissions.l2 && (
                <TableCell
                  width="5%"
                  sx={{
                    fontWeight: "bold",
                    border: "1px solid #e0e0e0",
                    textAlign: "center",
                    padding: "4px",
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isAllSelected}
                    indeterminate={isIndeterminate}
                    onChange={handleSelectAll}
                    disabled={subtypesData.every((item) =>
                      disbursedIds.includes(item.id),
                    )}
                  />
                </TableCell>
              )}

              <TableCell
                width="8%"
                sx={{
                  fontWeight: "bold",
                  border: "1px solid #e0e0e0",
                  textAlign: "center",
                }}
              >
                Sr. No
              </TableCell>

              <TableCell
                width="15%"
                sx={{
                  fontWeight: "bold",
                  border: "1px solid #e0e0e0",
                  textAlign: "center",
                }}
              >
                Subtypes
              </TableCell>

              <TableCell
                width="30%"
                sx={{
                  fontWeight: "bold",
                  border: "1px solid #e0e0e0",
                  textAlign: "center",
                }}
              >
                Requirement Details
              </TableCell>

              <TableCell
                width="15%"
                sx={{
                  fontWeight: "bold",
                  border: "1px solid #e0e0e0",
                  textAlign: "center",
                }}
              >
                Amount
              </TableCell>

              <TableCell
                width="20%"
                sx={{
                  fontWeight: "bold",
                  border: "1px solid #e0e0e0",
                  textAlign: "center",
                }}
              >
                Remark
              </TableCell>

              {hasRedFlag && (
                <TableCell
                  width="30%"
                  sx={{
                    fontWeight: "bold",
                    border: "1px solid #e0e0e0",
                    textAlign: "center",
                  }}
                >
                  Red Flag Reason
                </TableCell>
              )}

              <TableCell
                width="7%"
                sx={{
                  fontWeight: "bold",
                  border: "1px solid #e0e0e0",
                  textAlign: "center",
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {subtypesData.map((item, index) => (
              <TableRow
                key={index}
                selected={selectedRows.some((r) => r === item)}
                sx={{
                  backgroundColor: selectedRows.some((r) => r === item)
                    ? "#e3f2fd"
                    : "inherit",
                }}
              >
                {/* ✅ Row Checkbox */}
                {inputStates?.h2Status === 1 && permissions.l2 && (
                  <TableCell
                    sx={{
                      border: "1px solid #e0e0e0",
                      textAlign: "center",
                      padding: "4px",
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={selectedRows.some((r) => r === item)}
                      onChange={() => handleRowCheckbox(item)}
                      disabled={disbursedIds.includes(item.id)}
                    />
                  </TableCell>
                )}

                {/* rest of your cells remain exactly the same */}
                <TableCell
                  sx={{
                    border: "1px solid #e0e0e0",
                    textAlign: "center",
                    verticalAlign: "middle",
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1.2}
                  >
                    <span>{index + 1}</span>
                    {item.redFlagStatus === 1 && (
                      <FlagIcon sx={{ color: "red", fontSize: 18 }} />
                    )}
                    <VisibilityIcon
                      sx={{ color: "#1976d2", fontSize: 18, cursor: "pointer" }}
                      onClick={() => handleOpenEditModal(index)}
                    />
                  </Box>
                </TableCell>

                <TableCell sx={{ border: "1px solid #e0e0e0" }}>
                  <Autocomplete
                    size="small"
                    options={SUBTYPE_OPTIONS}
                    value={item.subTypes || null}
                    disabled={saveDisabledCon || (notRequster && !permissions.l1)}
                    onChange={(e, newValue) =>
                      handleSubtypeChange(index, "subTypes", newValue || "")
                    }
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select subtype" />
                    )}
                  />
                </TableCell>

                <TableCell sx={{ border: "1px solid #e0e0e0" }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Enter description"
                    value={item.description}
                    disabled={saveDisabledCon || (notRequster && !permissions.l1)}
                    onChange={(e) =>
                      handleSubtypeChange(index, "description", e.target.value)
                    }
                  />
                </TableCell>

                <TableCell sx={{ border: "1px solid #e0e0e0" }}>
                  <TextField
                    size="small"
                    type="number"
                    placeholder="0.00"
                    value={item.amount}
                    disabled={saveDisabledCon}
                    onChange={(e) =>
                      handleSubtypeChange(index, "amount", e.target.value)
                    }
                  />
                </TableCell>

                <TableCell sx={{ border: "1px solid #e0e0e0" }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Enter remark"
                    value={item.remark || ""}
                    disabled={

                      (!permissions?.h1 && !permissions?.h2)
                    }
                    onChange={(e) =>
                      handleSubtypeChange(index, "remark", e.target.value)
                    }
                  />
                </TableCell>

                {hasRedFlag && (
                  <TableCell sx={{ border: "1px solid #e0e0e0" }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Enter red flag reason"
                      value={item.redFlagDetails || ""}
                      disabled={
                        !item.redFlagStatus ||
                        saveDisabledCon ||
                        !(showRedFlagColumn && hasRedFlag)
                      }
                      onChange={(e) =>
                        handleSubtypeChange(
                          index,
                          "redFlagDetails",
                          e.target.value,
                        )
                      }
                    />
                  </TableCell>
                )}

                <TableCell align="center" sx={{ border: "1px solid #e0e0e0" }}>
                  <Box display="flex" justifyContent="center" gap={1}>
                    {showRedFlagColumn && (
                      <IconButton
                        size="small"
                        onClick={() => toggleRedFlag(index)}
                      >
                        <FlagIcon
                          sx={{ color: item.redFlagStatus ? "red" : "#9e9e9e" }}
                        />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      disabled={iconL1Disabled}
                      onClick={() => uploadDocInRow(index)}
                    >
                      <CloudUploadIcon
                        sx={{ color: iconL1Disabled ? "#bdbdbd" : "#0288d1" }}
                      />
                    </IconButton>
                    <input
                      type="file"
                      id={`upload-doc-${index}`}
                      hidden
                      onChange={(e) => handleRowFileUpload(e, index)}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (!item?.documentUlr) {
                          toast.warning("No document uploaded for this row");
                          return;
                        }
                        window.open(
                          item.documentUlr,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      }}
                    >
                      <VisibilityIcon sx={{ color: "#0288d1" }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={iconDisabled}
                      onClick={() => copySubtypeRow(index)}
                    >
                      <ContentCopyIcon
                        sx={{ color: iconDisabled ? "#bdbdbd" : "#1976d2" }}
                      />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={iconDisabled}
                      onClick={() => deleteSubtypeRow(index)}
                    >
                      <DeleteIcon
                        sx={{ color: iconDisabled ? "#bdbdbd" : "red" }}
                      />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="flex-end" mt={2}>
        <Typography variant="subtitle1" fontWeight="bold">
          Total Amount: {totalAmount.toFixed(2)}
        </Typography>
      </Box>

      {/* Dialog remains exactly the same */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        fullWidth
        maxWidth="md"
      >
        {/* Header with Close Button */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">Edit Subtype</h2>

          <CloseIcon
            className="text-red-500 cursor-pointer hover:scale-110 transition"
            onClick={() => setEditModalOpen(false)}
          />
        </div>

        <DialogContent>
          {selectedRowIndex !== null && (
            <div className="flex flex-col gap-4 w-full mt-2">
              {/* Subtype + Amount row */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="w-full sm:w-1/2">
                  <Autocomplete
                    size="small"
                    options={SUBTYPE_OPTIONS}
                    disabled={saveDisabledCon || (notRequster && !permissions.l1)}
                    value={subtypesData[selectedRowIndex]?.subTypes || null}
                    onChange={(e, newValue) =>
                      handleSubtypeChange(
                        selectedRowIndex,
                        "subTypes",
                        newValue || "",
                      )
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Subtype" fullWidth />
                    )}
                  />
                </div>

                <div className="w-full sm:w-1/2">
                  <TextField
                    label="Amount"
                    type="number"
                    value={subtypesData[selectedRowIndex]?.amount || ""}
                    disabled={saveDisabledCon}
                    onChange={(e) =>
                      handleSubtypeChange(
                        selectedRowIndex,
                        "amount",
                        e.target.value,
                      )
                    }
                    fullWidth
                    size="small"
                  />
                </div>
              </div>

              <TextField
                label="Remark"
                value={subtypesData[selectedRowIndex]?.remark || ""}
                disabled={

                  (!permissions?.h1 && !permissions?.h2)
                }
                onChange={(e) =>
                  handleSubtypeChange(
                    selectedRowIndex,
                    "remark",
                    e.target.value,
                  )
                }
                fullWidth
                size="small"
                multiline
                minRows={2}
              />

              {/* Description */}
              <TextField
                label="Description"
                value={subtypesData[selectedRowIndex]?.description || ""}
                disabled={saveDisabledCon || (notRequster && !permissions.l1)}
                onChange={(e) =>
                  handleSubtypeChange(
                    selectedRowIndex,
                    "description",
                    e.target.value,
                  )
                }
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
              {/* Red Flag Details */}
              <TextField
                label="Red Flag Details"
                value={subtypesData[selectedRowIndex]?.redFlagDetails || ""}
                disabled={saveDisabledCon || !(showRedFlagColumn && hasRedFlag)}
                onChange={(e) =>
                  handleSubtypeChange(
                    selectedRowIndex,
                    "redFlagDetails",
                    e.target.value,
                  )
                }
                fullWidth
                size="small"
                multiline
                minRows={2}
              />
              {/* Actions Section */}
              <div className="flex flex-wrap justify-center gap-4 pt-4 border-t">
                {/* Flag (ALWAYS ACTIVE) */}
                {showRedFlagColumn && (
                  <FlagIcon
                    className={`cursor-pointer ${subtypesData[selectedRowIndex]?.redFlagStatus
                        ? "text-red-500"
                        : "text-gray-400"
                      }`}
                    onClick={() => toggleRedFlag(selectedRowIndex)}
                  />
                )}

                {/* Upload */}
                <CloudUploadIcon
                  className={`${iconL1Disabled
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-blue-600 cursor-pointer"
                    }`}
                  onClick={() => {
                    if (iconL1Disabled) return;
                    uploadDocInRow(selectedRowIndex);
                  }}
                />

                <input
                  type="file"
                  id={`upload-doc-${selectedRowIndex}`}
                  hidden
                  onChange={(e) => handleRowFileUpload(e, selectedRowIndex)}
                />

                {/* View */}
                <VisibilityIcon
                  // className={`${
                  //   iconDisabled
                  //     ? "text-gray-400 cursor-not-allowed"
                  //     : "text-blue-600 cursor-pointer"
                  // }`}

                  className={`${"text-blue-600 cursor-pointer"}`}
                  onClick={() => {
                    // if (iconDisabled) return;

                    const item = subtypesData[selectedRowIndex];

                    if (!item?.documentUlr) {
                      toast.warning("No document uploaded for this row");
                      return;
                    }

                    window.open(
                      item.documentUlr,
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }}
                />

                {/* Copy */}
                <ContentCopyIcon
                  className={`${iconDisabled
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-blue-700 cursor-pointer"
                    }`}
                  onClick={() => {
                    if (iconDisabled) return;
                    copySubtypeRow(selectedRowIndex);
                  }}
                />

                {/* Delete */}
                <DeleteIcon
                  className={`${iconDisabled
                      ? "text-gray-400 cursor-not-allowed"
                      : "text-red-500 cursor-pointer"
                    }`}
                  onClick={() => {
                    if (iconDisabled) return;
                    deleteSubtypeRow(selectedRowIndex);
                    setEditModalOpen(false);
                  }}
                />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Subtypes;
